import { type ReactNode } from "react";
import { useGamificationToast, type GamificationToast, type XPGainData, type StreakData, type ChallengeProgressData, type FreezeUsedData, type MilestoneAchievedData } from "@/contexts/GamificationToastContext";
import { cn } from "@/lib/utils";
import { Sparkles, Flame, Trophy, Star, X, CheckCircle2, Gift, Snowflake, Award } from "lucide-react";

/**
 * Two tones, both from the product palette.
 *
 * These seven toasts used to carry six different gradients — violet→purple for
 * XP, orange→red for streaks, yellow→amber→orange for level-ups, blue→cyan for
 * challenges — none of which appear anywhere else in the app. A celebration is
 * the one moment the amber accent is allowed to own a surface, so "reward"
 * takes the warm wash (`identityWashClass`'s palette) and everything else is
 * paper. The icon well carries the state; the card stays quiet.
 */
type ToastTone = "reward" | "progress";

const TONE: Record<
  ToastTone,
  { shell: string; well: string; meta: string; star: string }
> = {
  reward: {
    shell:
      "border-amber-200/70 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/40",
    well:
      "bg-amber-500/15 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300",
    meta: "text-amber-900 dark:text-amber-100",
    star: "text-amber-400",
  },
  progress: {
    shell:
      "border-stone-200/80 bg-white dark:border-stone-800 dark:bg-stone-900",
    well:
      "bg-primary/10 text-primary-strong dark:bg-primary/20 dark:text-blue-300",
    meta: "text-stone-600 dark:text-stone-400",
    star: "text-primary",
  },
};

interface ToastCardProps {
  tone: ToastTone;
  icon: ReactNode;
  title: ReactNode;
  meta: ReactNode;
  extra?: ReactNode;
  stars?: number;
  onDismiss: () => void;
}

/**
 * One shell for all seven toasts. It used to be seven hand-rolled copies of the
 * same card, so every palette change had to be made seven times.
 */
const ToastCard = ({
  tone,
  icon,
  title,
  meta,
  extra,
  stars = 0,
  onDismiss,
}: ToastCardProps) => {
  const t = TONE[tone];

  return (
    <div
      className={cn(
        "relative flex items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3",
        "shadow-lg shadow-stone-900/10",
        "animate-in slide-in-from-top-2 fade-in duration-300 motion-reduce:animate-none",
        t.shell
      )}
    >
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
          t.well
        )}
      >
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold leading-tight tracking-tight text-stone-900 dark:text-stone-50">
          {title}
        </div>
        <div className={cn("mt-0.5 text-xs leading-snug", t.meta)}>{meta}</div>
        {extra}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-full p-1 text-stone-500 transition-colors hover:bg-stone-900/5 hover:text-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-100"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      {stars > 0 && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-1.5 left-1/2 flex -translate-x-1/2 gap-1"
        >
          {Array.from({ length: stars }).map((_, i) => (
            <Star
              key={i}
              className={cn(
                "h-2.5 w-2.5 animate-ping motion-reduce:hidden",
                t.star
              )}
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ToastItem = ({
  toast,
  onDismiss,
}: {
  toast: GamificationToast;
  onDismiss: () => void;
}) => {
  if (toast.type === "xp") {
    const data = toast.data as XPGainData;
    return (
      <ToastCard
        tone="reward"
        icon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
        title={
          <span className="text-lg tabular-nums">+{data.xpAmount} XP</span>
        }
        meta={`Total: ${data.newXp} XP`}
        onDismiss={onDismiss}
      />
    );
  }

  if (toast.type === "streak") {
    const data = toast.data as StreakData;
    return (
      <ToastCard
        tone="reward"
        icon={<Flame className="h-5 w-5" aria-hidden="true" />}
        title={`${data.currentStreak} Day Streak!`}
        meta={data.isNewRecord ? "New record!" : "Keep it going!"}
        onDismiss={onDismiss}
      />
    );
  }

  if (toast.type === "level-up") {
    const data = toast.data as { message: string };
    return (
      <ToastCard
        tone="reward"
        icon={<Trophy className="h-5 w-5" aria-hidden="true" />}
        title="Level Up!"
        meta={`You reached ${data.message}`}
        stars={5}
        onDismiss={onDismiss}
      />
    );
  }

  if (toast.type === "challenge-complete") {
    const data = toast.data as { message: string };
    return (
      <ToastCard
        tone="progress"
        icon={<Trophy className="h-5 w-5" aria-hidden="true" />}
        title="Challenge Complete!"
        meta={`Earned ${data.message}`}
        onDismiss={onDismiss}
      />
    );
  }

  if (toast.type === "challenge-progress") {
    const data = toast.data as ChallengeProgressData;
    return (
      <ToastCard
        tone="progress"
        icon={<CheckCircle2 className="h-5 w-5" aria-hidden="true" />}
        title="Challenge Ready!"
        meta={data.description}
        extra={
          <div className="mt-1 flex items-center gap-1 text-xs text-stone-600 dark:text-stone-400">
            <Gift className="h-3 w-3" aria-hidden="true" />
            <span>+{data.xpReward} XP claimed automatically</span>
          </div>
        }
        onDismiss={onDismiss}
      />
    );
  }

  if (toast.type === "freeze-used") {
    const data = toast.data as FreezeUsedData;
    return (
      <ToastCard
        tone="progress"
        icon={<Snowflake className="h-5 w-5" aria-hidden="true" />}
        title="Streak Freeze Used!"
        meta={`Your streak was protected. ${data.freezesRemaining} freeze${
          data.freezesRemaining !== 1 ? "s" : ""
        } remaining.`}
        onDismiss={onDismiss}
      />
    );
  }

  if (toast.type === "milestone-achieved") {
    const data = toast.data as MilestoneAchievedData;
    return (
      <ToastCard
        tone="reward"
        icon={<Award className="h-5 w-5" aria-hidden="true" />}
        title={`${data.badgeName}!`}
        meta={`${data.streakDays} day streak milestone`}
        extra={
          <div className="mt-0.5 flex items-center gap-2 text-xs text-amber-900 dark:text-amber-100">
            {data.xpBonus > 0 && <span>+{data.xpBonus} XP</span>}
            {data.freezesAwarded > 0 && (
              <span>+{data.freezesAwarded} freeze</span>
            )}
          </div>
        }
        stars={3}
        onDismiss={onDismiss}
      />
    );
  }

  return null;
};

export const GamificationToasts = () => {
  const { toasts, dismissToast } = useGamificationToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 z-[100] flex -translate-x-1/2 flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            toast={toast}
            onDismiss={() => dismissToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

export default GamificationToasts;
